import { focusHaloFlush, panelMotion } from "@pax/ui";
import { ArrowDown, ArrowUp, ChevronDown, ChevronRight } from "lucide-react";
import type { ReactNode } from "react";
import type { Dimension } from "./dimensions";
import {
  type Column,
  emptyTotals,
  hasCalls,
  type MetricInfo,
  percent,
  plural,
  type Totals,
} from "./measures";
import { Mark } from "./pieces";
import { OTHER, type Range, type Row, ranges } from "./queries";

const shadeFills = [
  "bg-foreground/80",
  "bg-foreground/64",
  "bg-foreground/50",
  "bg-foreground/38",
  "bg-foreground/28",
  "bg-foreground/20",
  "bg-foreground/14",
  "bg-foreground/10",
];
const shadeStrokes = [
  "stroke-foreground/80",
  "stroke-foreground/64",
  "stroke-foreground/50",
  "stroke-foreground/38",
  "stroke-foreground/28",
  "stroke-foreground/20",
  "stroke-foreground/14",
  "stroke-foreground/10",
];
const otherFill = "bg-foreground/[0.06]";
const otherStroke = "stroke-foreground/30";

const fillOf = (row: Row, index: number) =>
  row.others ? otherFill : (shadeFills[index] ?? otherFill);
const strokeOf = (row: Row, index: number) =>
  row.others ? otherStroke : (shadeStrokes[index] ?? otherStroke);

/** A measure's reading, or a dash where there's nothing to read. */
export function reading(metric: MetricInfo, totals: Totals, slowest: boolean): string {
  const value = hasCalls(totals) ? metric.value(totals, slowest) : null;
  return value === null ? "–" : metric.format(value);
}

/** How a measure moved against the window before, in words. */
export function changeText(
  metric: MetricInfo,
  now: Totals,
  before: Totals | undefined,
  range: Range,
): string {
  const value = metric.value(now, false);
  const earlier = before && hasCalls(before) ? metric.value(before, false) : null;
  if (value === null || earlier === null) return `Nothing to compare with ${ranges[range].before}`;
  const change = metric.inPoints ? value - earlier : earlier > 0 ? (value - earlier) / earlier : 0;
  if (Math.abs(change) < 0.005) return `The same as ${ranges[range].before}`;
  const amount = metric.inPoints
    ? plural(Math.max(1, Math.round(Math.abs(change) * 100)), "point")
    : percent(Math.abs(change));
  return `${amount} ${metric.change[change > 0 ? 0 : 1]} than ${ranges[range].before}`;
}

function niceTicks(high: number): number[] {
  if (high <= 0) return [];
  const rough = high / 3;
  const magnitude = 10 ** Math.floor(Math.log10(rough));
  const step =
    [1, 2, 2.5, 5, 10].map(factor => factor * magnitude).find(size => size >= rough) ?? rough;
  return Array.from({ length: Math.floor(high / step) }, (_, index) => (index + 1) * step);
}

/**
 * Stacked bars for anything that adds up, lines for anything that averages. Pointing at a moment
 * hands it to the rest of the page.
 */
export function Chart({
  rows,
  metric,
  slowest,
  hoveredBucket,
  onHoverBucket,
  hoveredRow,
}: {
  rows: Row[];
  metric: MetricInfo;
  slowest: boolean;
  hoveredBucket: number | null;
  onHoverBucket: (index: number | null) => void;
  hoveredRow: string | null;
}) {
  const series = rows;
  const count = series[0]?.buckets.length ?? 0;
  const values = series.map(row =>
    row.buckets.map(totals =>
      hasCalls(totals) ? (metric.value(totals, slowest) ?? Number.NaN) : Number.NaN,
    ),
  );
  const stackPeaks = Array.from({ length: count }, (_, index) =>
    values.reduce((total, points) => total + (points[index] || 0), 0),
  );
  const linePeak = Math.max(...values.flat().filter(value => !Number.isNaN(value)), 0);
  const peak = (metric.additive ? Math.max(...stackPeaks, 0) : linePeak) * 1.12 || 1;
  const ticks = niceTicks(peak);
  const dim = (key: string) => hoveredRow !== null && hoveredRow !== key;

  return (
    <div className="relative mt-4 h-40">
      {ticks.map(tick => (
        <div
          key={tick}
          className="absolute inset-x-0 flex translate-y-1/2 items-center gap-2"
          style={{ bottom: `${(tick / peak) * 100}%` }}
        >
          <span className="flex-1 border-t border-dashed border-foreground/8" />
          <span className="w-10 text-[10px] leading-none text-muted-foreground/70 tabular-nums">
            {metric.format(tick)}
          </span>
        </div>
      ))}

      <div className="absolute inset-y-0 right-12 left-0">
        {metric.additive ? (
          <div className="absolute inset-0 flex items-end gap-[3px]">
            {Array.from({ length: count }, (_, index) => (
              <div
                // biome-ignore lint/suspicious/noArrayIndexKey: a bucket is its position
                key={index}
                className={`flex h-full flex-1 flex-col-reverse ${panelMotion} ${
                  hoveredBucket !== null && hoveredBucket !== index ? "opacity-45" : ""
                }`}
              >
                {series.map((row, rowIndex) => (
                  <div
                    key={row.key}
                    className={`${fillOf(row, rowIndex)} ${panelMotion} ${
                      dim(row.key) ? "opacity-25" : ""
                    } ${rowIndex === series.length - 1 ? "rounded-t-[3px]" : ""}`}
                    style={{ height: `${((values[rowIndex]?.[index] || 0) / peak) * 100}%` }}
                  />
                ))}
              </div>
            ))}
          </div>
        ) : (
          <svg
            viewBox="0 0 100 100"
            preserveAspectRatio="none"
            className="absolute inset-0 size-full overflow-visible"
            role="img"
          >
            <title>{metric.label}</title>
            {[...series].reverse().map((row, reversedIndex) => {
              const rowIndex = series.length - 1 - reversedIndex;
              const points = values[rowIndex] ?? [];
              return (
                <path
                  key={row.key}
                  fill="none"
                  vectorEffect="non-scaling-stroke"
                  strokeWidth={hoveredRow === row.key || series.length === 1 ? 2 : 1.5}
                  strokeLinejoin="round"
                  strokeDasharray={row.others ? "3 3" : undefined}
                  className={`${
                    hoveredRow === row.key ? "stroke-foreground" : strokeOf(row, rowIndex)
                  } ${panelMotion} ${dim(row.key) ? "opacity-20" : ""}`}
                  d={points
                    .map((value, index) => {
                      if (Number.isNaN(value)) return "";
                      const x = ((index + 0.5) / count) * 100;
                      const y = 100 - (value / peak) * 100;
                      const joinsBefore = !Number.isNaN(points[index - 1] ?? Number.NaN);
                      const joinsAfter = !Number.isNaN(points[index + 1] ?? Number.NaN);
                      if (joinsBefore) return `L${x},${y}`;
                      if (joinsAfter) return `M${x},${y}`;
                      const half = 30 / count;
                      return `M${x - half},${y} L${x + half},${y}`;
                    })
                    .join(" ")}
                />
              );
            })}
          </svg>
        )}

        {!metric.additive && hoveredBucket !== null && (
          <div
            className="pointer-events-none absolute inset-y-0 w-px bg-foreground/20"
            style={{ left: `${((hoveredBucket + 0.5) / count) * 100}%` }}
          />
        )}

        {/* biome-ignore lint/a11y/noStaticElementInteractions: pointing only changes what the page reads out */}
        <div className="absolute inset-0 flex" onMouseLeave={() => onHoverBucket(null)}>
          {Array.from({ length: count }, (_, index) => (
            // biome-ignore lint/a11y/noStaticElementInteractions: pointing only changes what the page reads out
            <div
              // biome-ignore lint/suspicious/noArrayIndexKey: a bucket is its position
              key={index}
              className="h-full flex-1"
              onMouseEnter={() => onHoverBucket(index)}
            />
          ))}
        </div>
      </div>
    </div>
  );
}

/**
 * One row per value of the split, and the legend for the chart above it. Pressing a column
 * charts it, and pressing the charted column again turns its sort around. Pressing a row opens its
 * profile. Pointing at a moment on the chart turns every number here into that moment's.
 */
export function Table({
  rows,
  columns,
  charted,
  ascending,
  onChart,
  bucket,
  tokenSplit,
  showOthers,
  onToggleOthers,
  onHoverRow,
  onOpen,
}: {
  rows: Row[];
  columns: Column[];
  /** The column the chart above draws, which the rows are sorted by. */
  charted: string;
  /** Smallest first. The rows stay the same top eight, each with its shade. */
  ascending: boolean;
  onChart: (column: string) => void;
  bucket: number | null;
  tokenSplit: boolean;
  /** Whether the Other row is open, listing everything it adds up. */
  showOthers: boolean;
  onToggleOthers: () => void;
  onHoverRow: (key: string | null) => void;
  onOpen: (dimension: Dimension, value: string) => void;
}) {
  const applies = (column: Column) => !tokenSplit || column.perTokenKind === true;
  const cell = (column: Column) =>
    column.narrow || column.id === charted
      ? "w-[4.25rem] shrink-0"
      : "hidden w-[4.25rem] shrink-0 @2xl:block";
  const shared = { columns, cell, applies, charted, bucket, onHoverRow, onOpen };
  const shown = rows.map((row, index) => ({ row, fill: fillOf(row, index) }));
  const named = shown.filter(({ row }) => !row.others);
  const ordered = [
    ...(ascending ? named.reverse() : named),
    ...shown.filter(({ row }) => row.others),
  ];

  return (
    <div className="@container -mx-3 mt-6">
      <div className="flex items-end px-3 pb-1.5">
        <span className="min-w-0 flex-1" />
        {columns.map(column => (
          <span key={column.id} className={`${cell(column)} group relative`}>
            <button
              type="button"
              onClick={() => onChart(column.id)}
              aria-pressed={column.id === charted}
              aria-describedby={`definition-${column.id}`}
              className={`w-full truncate rounded-md text-right text-xs ${panelMotion} ${focusHaloFlush} ${
                column.id === charted
                  ? "font-medium text-foreground"
                  : "text-muted-foreground hover:text-foreground"
              }`}
            >
              {column.id === charted &&
                (ascending ? (
                  <ArrowUp aria-hidden className="mr-0.5 inline size-3 align-[-2px]" />
                ) : (
                  <ArrowDown aria-hidden className="mr-0.5 inline size-3 align-[-2px]" />
                ))}
              {column.column}
            </button>
            <span
              id={`definition-${column.id}`}
              role="tooltip"
              className="pointer-events-none absolute right-0 bottom-full z-30 mb-2 w-64 rounded-xl bg-popover px-3 py-2 text-left text-xs leading-relaxed text-popover-foreground opacity-0 shadow-lg ring-1 ring-foreground/10 transition-opacity duration-150 group-focus-within:opacity-100 group-hover:opacity-100 group-hover:delay-300"
            >
              <span className="block font-medium">{column.label}</span>
              <span className="mt-0.5 block text-muted-foreground">{column.definition}</span>
            </span>
          </span>
        ))}
      </div>
      {/* biome-ignore lint/a11y/noStaticElementInteractions: pointing only highlights the chart */}
      <div onMouseLeave={() => onHoverRow(null)}>
        {ordered.flatMap(({ row, fill }) => {
          if (!row.others) return [<TableRow key={row.key} row={row} fill={fill} {...shared} />];
          return [
            <TableRow
              key={row.key}
              row={row}
              fill={fill}
              {...shared}
              trailing={
                <ChevronDown
                  className={`size-3.5 shrink-0 text-muted-foreground ${panelMotion} ${
                    showOthers ? "rotate-180" : ""
                  }`}
                />
              }
              onPress={onToggleOthers}
            />,
            ...(showOthers
              ? row.others.map(other => (
                  <TableRow
                    key={other.key}
                    row={other}
                    fill={fill}
                    {...shared}
                    hoverKey={OTHER}
                    nested
                  />
                ))
              : []),
          ];
        })}
      </div>
    </div>
  );
}

function TableRow({
  row,
  fill,
  columns,
  cell,
  applies,
  charted,
  bucket,
  onHoverRow,
  onOpen,
  hoverKey = row.key,
  nested = false,
  trailing,
  onPress,
}: {
  row: Row;
  /** The row's shade in the chart. */
  fill: string;
  columns: Column[];
  cell: (column: Column) => string;
  applies: (column: Column) => boolean;
  charted: string;
  bucket: number | null;
  onHoverRow: (key: string | null) => void;
  onOpen: (dimension: Dimension, value: string) => void;
  /** The chart series this row lights up, which for a row inside Other is Other itself. */
  hoverKey?: string;
  nested?: boolean;
  trailing?: ReactNode;
  onPress?: () => void;
}) {
  const totals = bucket === null ? row.window : (row.buckets[bucket] ?? emptyTotals());
  const { dimension } = row;
  const pressRow = onPress ?? (dimension ? () => onOpen(dimension, row.key) : undefined);
  const content = (
    <>
      <span className={`flex min-w-0 flex-1 items-center gap-2.5 ${nested ? "pl-4" : ""}`}>
        <span aria-hidden className={`h-4 w-1 shrink-0 rounded-full ${fill}`} />
        <Mark src={row.icon} />
        <span className="min-w-0">
          <span className={`block truncate ${row.others ? "text-muted-foreground" : ""}`}>
            {row.label}
          </span>
          {row.about && (
            <span className="block truncate text-xs text-muted-foreground">{row.about}</span>
          )}
        </span>
        {trailing ??
          (dimension && (
            <ChevronRight
              className={`size-3.5 shrink-0 text-muted-foreground opacity-0 ${panelMotion} group-hover:opacity-100`}
            />
          ))}
      </span>
      {columns.map(column => (
        <span
          key={column.id}
          className={`${cell(column)} text-right tabular-nums ${
            column.id === charted ? "font-medium" : "text-muted-foreground"
          }`}
        >
          {applies(column) ? reading(column, totals, false) : "–"}
        </span>
      ))}
    </>
  );
  const className = `group flex w-full items-center rounded-xl px-3 py-2 text-left text-sm ${panelMotion}`;
  return pressRow ? (
    <button
      type="button"
      onMouseEnter={() => onHoverRow(hoverKey)}
      onFocus={() => onHoverRow(hoverKey)}
      onClick={pressRow}
      className={`${className} hover:bg-foreground/5 ${focusHaloFlush}`}
    >
      {content}
    </button>
  ) : (
    // biome-ignore lint/a11y/noStaticElementInteractions: pointing only highlights the chart
    <div onMouseEnter={() => onHoverRow(hoverKey)} className={className}>
      {content}
    </div>
  );
}

/** A chart's own key: its shades, each row's mark and name, and the number it comes to. */
export function Legend({
  rows,
  metric,
  slowest,
  bucket,
  onHoverRow,
  onOpen,
}: {
  rows: Row[];
  metric: MetricInfo;
  slowest: boolean;
  bucket: number | null;
  onHoverRow: (key: string | null) => void;
  onOpen: (dimension: Dimension, value: string) => void;
}) {
  return (
    // biome-ignore lint/a11y/noStaticElementInteractions: pointing only highlights the chart
    <div className="mt-3 grid grid-cols-2 gap-x-6" onMouseLeave={() => onHoverRow(null)}>
      {rows.map((row, index) => {
        const totals = bucket === null ? row.window : (row.buckets[bucket] ?? emptyTotals());
        const opens = row.dimension && !row.others ? row.dimension : undefined;
        return (
          <button
            key={row.key}
            type="button"
            disabled={!opens}
            onClick={() => opens && onOpen(opens, row.key)}
            onMouseEnter={() => onHoverRow(row.key)}
            onFocus={() => onHoverRow(row.key)}
            className={`-mx-2 flex min-w-0 items-center gap-2 rounded-lg px-2 py-1 text-left text-xs enabled:hover:bg-foreground/5 ${panelMotion} ${focusHaloFlush}`}
          >
            <span aria-hidden className={`h-3 w-1 shrink-0 rounded-full ${fillOf(row, index)}`} />
            <Mark src={row.icon} size="size-3.5" />
            <span
              className={`min-w-0 flex-1 truncate ${row.others ? "text-muted-foreground" : ""}`}
            >
              {row.others ? row.about : row.label}
            </span>
            <span className="text-muted-foreground tabular-nums">
              {reading(metric, totals, slowest)}
            </span>
          </button>
        );
      })}
    </div>
  );
}
